import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router';

import { SectionTitle } from '~/components/section-title/SectionTitle';
import { getKnowledgeGraphSearchParams } from '~/modules/overview-knowledge-graph/utils/knowledge-graph-utils';
import { useBrandsAndProducts } from '~/react-query-hooks/knowledge-graph/useBrandsAndProducts';
import { appPaths } from '~/routes/app-paths';
import { useBrandAndProductDrawer } from '~/widgets/knowledge-graph-widget/components/brands-and-products-tab/useBrandAndProductDrawer';
import { KnowledgeGraphTabType } from '~/widgets/knowledge-graph-widget/KnowledgeGraphWidget';

import * as S from './BrandsAndProductsSection.styles';
import { ProductCard } from './components/ProductCard';

/**
 * @deprecated
 */
export const BrandsAndProductsSection = ({
  entityTag,
  isInDrawer = false,
}: {
  entityTag: string;
  isInDrawer?: boolean;
}) => {
  const { t } = useTranslation(['widget']);
  const [searchParams] = useSearchParams();
  const { openDrawer } = useBrandAndProductDrawer();
  const navigate = useNavigate();

  const {
    data: brandsAndProducts = [],
    isFetching,
    isError,
  } = useBrandsAndProducts(entityTag);

  const connectionsSearchParams = getKnowledgeGraphSearchParams({
    entityTag,
    searchParams,
    subtab: KnowledgeGraphTabType.BrandsAndProducts,
  }).toString();

  const linkToConnections = `${appPaths.assetOverview}?${connectionsSearchParams}`;

  const topItems = [...brandsAndProducts]
    .sort((a, b) => a.relationship.rank - b.relationship.rank)
    .slice(0, 3);

  const getContent = () => {
    if (isFetching) {
      return <S.LoadingSkeleton data-testid="loading-skeleton" />;
    }

    if (isError || !topItems.length) {
      return null;
    }

    return (
      <S.ItemsWrapper data-testid="brands-products-wrapper">
        {topItems.map((item, index) => {
          const entityRank = index + 1;
          return (
            <ProductCard
              key={item.relationship.id}
              name={item.product}
              rank={entityRank}
              onClick={() =>
                isInDrawer
                  ? navigate(linkToConnections)
                  : openDrawer(item.product)
              }
            />
          );
        })}
      </S.ItemsWrapper>
    );
  };

  if (!isFetching && (isError || !topItems.length)) {
    return null;
  }

  return (
    <S.SectionRow>
      <SectionTitle
        title={t(
          'widget:assetOverview.knowledgeGraph.subtab.brandsAndProducts'
        )}
        variant="small"
        linkTo={linkToConnections}
      />
      {getContent()}
    </S.SectionRow>
  );
};
